import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { apiFetch } from "../api/client";

interface AuthConfig {
  googleClientId: string;
}

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (opts: {
            client_id: string;
            callback: (res: { credential: string }) => void;
          }) => void;
          renderButton: (el: HTMLElement, opts: Record<string, unknown>) => void;
          prompt: () => void;
        };
      };
    };
  }
}

const GIS_SRC = "https://accounts.google.com/gsi/client";

export function LoginScreen() {
  const [error, setError] = useState<string | null>(null);
  const [ready, setReady] = useState(false);
  const buttonRef = useRef<HTMLDivElement>(null);
  const queryClient = useQueryClient();

  useEffect(() => {
    let cancelled = false;

    async function bootstrap() {
      try {
        const cfg = await apiFetch<AuthConfig>("/api/dev/auth/config");

        if (!document.querySelector(`script[src="${GIS_SRC}"]`)) {
          const script = document.createElement("script");
          script.src = GIS_SRC;
          script.async = true;
          script.defer = true;
          document.head.appendChild(script);
          await new Promise<void>((resolve, reject) => {
            script.onload = () => resolve();
            script.onerror = () => reject(new Error("Failed to load Google Identity Services"));
          });
        } else if (!window.google?.accounts?.id) {
          await new Promise<void>((resolve) => {
            const t = setInterval(() => {
              if (window.google?.accounts?.id) {
                clearInterval(t);
                resolve();
              }
            }, 50);
          });
        }

        if (cancelled || !buttonRef.current || !window.google) return;

        window.google.accounts.id.initialize({
          client_id: cfg.googleClientId,
          callback: async (res) => {
            try {
              await apiFetch("/api/dev/auth/verify", {
                method: "POST",
                body: JSON.stringify({ idToken: res.credential }),
              });
              await queryClient.invalidateQueries({ queryKey: ["auth", "me"] });
            } catch (err) {
              setError(err instanceof Error ? err.message : String(err));
            }
          },
        });

        window.google.accounts.id.renderButton(buttonRef.current, {
          theme: "filled_black",
          size: "large",
          type: "standard",
          shape: "rectangular",
          text: "signin_with",
          width: 260,
        });

        setReady(true);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : String(err));
        }
      }
    }

    bootstrap();
    return () => {
      cancelled = true;
    };
  }, [queryClient]);

  return (
    <div className="login-screen">
      <div className="login-card">
        <h1>Jarvis Dev</h1>
        <p>Sign in with Google to continue.</p>
        <div ref={buttonRef} style={{ display: "flex", justifyContent: "center" }} />
        {!ready && !error && <div className="spinner" style={{ margin: "16px auto" }} />}
        {error && <div className="login-error">{error}</div>}
      </div>
    </div>
  );
}
